import { useQuery } from '@tanstack/react-query'
import { fetchProperties, type ExploreProperty } from '../explorer/data'
import type { PropertyCardFilter } from './config'

export type { ExploreProperty }

/**
 * The card renders an `ExploreProperty` — the same shape the explorer grid
 * consumes (shared data layer since #683; the card UI itself is the template
 * system in common/property-card since #822). Fetch the one property through
 * the listing endpoint's `include_property` filter (central), which returns it
 * in exactly that shape. No client-side shape translation.
 */
export async function fetchCardProperty(
  filter: PropertyCardFilter,
): Promise<ExploreProperty | null> {
  const res = await fetchProperties(
    filter.feed_id,
    // `include_property` isn't in the explorer VisitorFilter type, but
    // fetchProperties forwards every filter key to the query string verbatim.
    { include_property: filter.property } as unknown as Parameters<typeof fetchProperties>[1],
    1,
    1,
    filter.platform ?? undefined,
  )
  const property = res.result?.[0] ?? null

  // #742: fail loud on the wrong property. `include_property` narrows to one
  // property server-side, but a Central that ignored the filter (old build /
  // regression) would return the feed's FIRST property with no error — silently
  // showing the wrong listing. If the returned slug is present and doesn't match
  // what we asked for, treat it as a fetch failure so the widget shows its error
  // state instead of a plausible-but-wrong card. (Central honors the filter on
  // beta + prod today, so this is a guard, not the normal path.)
  if (property && property.slug != null && String(property.slug) !== String(filter.property)) {
    throw new Error(
      `property-card: include_property returned the wrong property (asked "${filter.property}", got "${property.slug}")`,
    )
  }

  return property
}

export function useFetchCardProperty(filter: PropertyCardFilter) {
  return useQuery({
    queryKey: ['property-card', filter],
    queryFn: () => fetchCardProperty(filter),
    // Fail fast (#679): a bad slug / asset-API env mismatch never self-heals.
    retry: 1,
  })
}
